"use client";

/**
 * Modal 컴포넌트
 * @description 가운데 뜨는 오버레이. 앱 DS `Modal` 의 웹 대응 — 바깥을 누르거나 `Esc`, 안의
 *   닫기 버튼으로 닫는다. `<dialog>` 라 포커스 가두기와 배경 비활성화는 브라우저가 해 준다.
 * @example
 * <Modal visible={open} onClose={close} label="Profile" footer={<Button … fullWidth />}>
 *   <ProfileSummary />
 * </Modal>
 */
import { X } from "lucide-react";
import { memo, useEffect, useRef } from "react";

import { modalStyles } from "./Modal.styles";
import type { ModalProps } from "./Modal.types";

import IconButton from "../../atoms/IconButton/IconButton";

const CLOSE_LABEL = "Close";

const Modal = memo<ModalProps>(
  ({
    visible,
    onClose,
    label,
    closeLabel = CLOSE_LABEL,
    children,
    footer,
    testID,
  }) => {
    const ref = useRef<HTMLDialogElement>(null);

    useEffect(() => {
      const dialog = ref.current;
      if (!dialog) return;
      if (visible && !dialog.open) dialog.showModal();
      if (!visible && dialog.open) dialog.close();
    }, [visible]);

    return (
      <dialog
        ref={ref}
        aria-label={label}
        onClose={onClose}
        // 배경(::backdrop)을 눌러 닫는다 — dialog 요소 자체가 배경 영역까지 차지한다.
        onClick={(event) => {
          if (event.target === ref.current) onClose();
        }}
        className={modalStyles.dialog}
        data-testid={testID}
      >
        <div className={modalStyles.surface}>
          <div className={modalStyles.close}>
            <IconButton
              icon={<X />}
              label={closeLabel}
              tone="ghost"
              size="sm"
              radius="circle"
              onClick={onClose}
            />
          </div>
          <div className={modalStyles.body}>{children}</div>
          {footer ? <div className={modalStyles.footer}>{footer}</div> : null}
        </div>
      </dialog>
    );
  },
);

Modal.displayName = "Modal";

export default Modal;
